import { fetchWithRetry } from "../client";
import { Noticia } from "./types/noticias";
import { cmsLang, esEnLocales, type Locale } from "@/i18n/config";

export async function fetchNoticias(lang: Locale): Promise<Noticia[]> {
  const res = await fetchWithRetry<{ data: Noticia[] }>(
    `/noticias?lang=${cmsLang(lang)}`,
  );
  return res.data ?? [];
}

/**
 * Traduce el slug de una noticia al idioma pedido.
 *
 * El CMS traduce el slug junto con el título pero conserva el `id`: la misma
 * noticia es `/es/noticias/tocumen-avanza-hacia-la-normalizacion…` y
 * `/en/noticias/tocumen-moves-toward-the-normalization…`. Como el selector de
 * idioma sólo reescribe el prefijo de la URL, al cambiar de idioma sobre el
 * detalle llegaba el slug del idioma anterior y la noticia no se encontraba.
 * Lo mismo con cualquier enlace compartido y abierto en el otro idioma.
 *
 * Sólo las noticias efectivamente traducidas cambian de slug; las demás lo
 * repiten en ambos idiomas y nunca llegan hasta aquí.
 *
 * Devuelve `null` si el slug no existe en ningún idioma —eso sí es un 404— o
 * si el CMS no responde: mejor la página de no encontrado que un error 500.
 */
export async function traducirSlugNoticia(
  slug: string,
  locale: Locale,
  /** Listado del idioma actual, ya pedido por quien llama: no se repite. */
  noticiasDelIdioma: Noticia[],
): Promise<string | null> {
  const actual = cmsLang(locale);
  const otros = esEnLocales.filter((lang) => lang !== actual);

  for (const lang of otros) {
    try {
      const noticias = await fetchNoticias(lang);
      const id = noticias.find((n) => n.slug === slug)?.id;
      if (id === undefined) continue;

      const equivalente = noticiasDelIdioma.find((n) => n.id === id);
      if (equivalente) return equivalente.slug;
    } catch {
      // CMS caído o respuesta rota: se sigue con el resto de idiomas.
    }
  }

  return null;
}
